/**
 * @file consent-preview.ts
 * @description Образец для предпросмотра редакции текстов согласия
 * (phase19.16, Р3г; uiux-r3g-consent-texts §3): какие сервисы «включены» у
 * образца, их категории и итоговый текст баннера. Текст собирает shared
 * `buildConsentBannerText(texts, …)` — та же сборка, что у витрины.
 */

import {
  buildConsentBannerText,
  COUNTER_SERVICES,
  SITE_CONSENT_CATEGORIES,
  type ConsentBannerTexts,
  type SiteConsentBannerKind,
  type SiteConsentCategory,
  type SiteCounterService,
} from "@chaiberi/shared";

/** Набор сервисов образца (uiux §3 «Сервисы [Метрика и VK ▾]»). */
export type PreviewServicesPreset = "metrika_vk" | "metrika" | "vk";

export const PREVIEW_SERVICES_OPTIONS: ReadonlyArray<{ value: PreviewServicesPreset; label: string }> = [
  { value: "metrika_vk", label: "Метрика и VK" },
  { value: "metrika", label: "Только Метрика" },
  { value: "vk", label: "Только VK" },
];

const PRESET_SERVICES: Readonly<Record<PreviewServicesPreset, readonly SiteCounterService[]>> = {
  metrika_vk: ["metrika", "vk"],
  metrika: ["metrika"],
  vk: ["vk"],
};

export interface PreviewCategory {
  id: SiteConsentCategory;
  /** Короткие названия — в тексте баннера («Яндекс Метрика, VK»). */
  shortServices: string[];
  /** Полные названия — в окне «Настроить». */
  services: string[];
}

/** Категории образца в порядке реестра с названиями сервисов. */
export function previewCategories(preset: PreviewServicesPreset): PreviewCategory[] {
  const services = PRESET_SERVICES[preset];
  return SITE_CONSENT_CATEGORIES.flatMap((id) => {
    const own = services.filter((s) => COUNTER_SERVICES[s].category === id);
    if (own.length === 0) return [];
    return [{ id, shortServices: own.map((s) => COUNTER_SERVICES[s].shortLabel), services: own.map((s) => COUNTER_SERVICES[s].label) }];
  });
}

/** «аналитики и рекламы» — для подсказки «{categories} → …». */
function joinRu(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} и ${items[items.length - 1]}`;
}

/** Во что превратятся вставки у образца: подсказка под фразой режима. */
export function previewPlaceholderValues(texts: ConsentBannerTexts, categories: readonly PreviewCategory[]) {
  return {
    categories: joinRu(categories.map((c) => texts.categoryGenitive[c.id])),
    services: categories.flatMap((c) => c.shortServices).join(", "),
  };
}

/** Текст баннера образца: вступление платформы (владелец своего не написал). */
export function previewBannerText(texts: ConsentBannerTexts, kind: SiteConsentBannerKind, categories: readonly PreviewCategory[]) {
  return buildConsentBannerText(texts, {
    kind,
    intro: "",
    categories: kind === "info" ? [] : categories.map((c) => ({ id: c.id, services: c.shortServices })),
  });
}

// ── Ширина и масштаб образца (повторный дефект QA D2) ──

/** Настоящая ширина образца, px: телефон и широкий экран (планшет — 728, с 1024 — 1024). */
export const PREVIEW_FRAME_WIDTH = { narrow: 360, tablet: 728, desktop: 1024 } as const;

/**
 * Масштаб образца под колонку: не больше 1 (шире не растягиваем); колонка
 * ещё не измерена (0) — 1, чтобы первый кадр не схлопнулся.
 */
export function previewScale(containerWidth: number, frameWidth: number): number {
  if (containerWidth <= 0 || frameWidth <= 0) return 1;
  return Math.min(1, containerWidth / frameWidth);
}
